import React from 'react'

// Icons for badges
const imgVector = "https://www.figma.com/api/mcp/asset/f095f65e-c526-43cb-a505-f0a8e5d7f315"
const imgGroup23638 = "https://www.figma.com/api/mcp/asset/8231e6ff-2548-41b6-b188-026f58e370cd"

const SkillRadarChart = ({ subskillData }) => {
  // Calculate skill statistics
  const masteredSkills = subskillData?.filter(skill => skill.is_correct).length || 2
  const gapsDetected = subskillData?.filter(skill => !skill.is_correct).length || 3

  // Use the actual skill names from data or fall back to defaults
  const getSkillName = (index) => {
    if (subskillData && subskillData[index]) {
      // Shorten long skill names for display
      const name = subskillData[index].subskill
      return name
    }

    // Default skill names
    const defaultNames = [
      'Product Thinking',
      'Prioritisation',
      'User Research',
      'Metrics',
      'Strategy'
    ]
    return defaultNames[index] || 'Skill ' + (index + 1)
  }

  // Get skill data mapped to positions
  const getSkillDataForPosition = (index) => {
    if (!subskillData || index >= subskillData.length) {
      return { skill_level: 1, is_correct: false }
    }
    return subskillData[index]
  }

  // Generate pentagon/hexagon points based on number of skills
  const generatePolygonPoints = (centerX, centerY, radius, numPoints = 5) => {
    const points = []
    const startAngle = -Math.PI / 2 // Start from top
    for (let i = 0; i < numPoints; i++) {
      const angle = startAngle + (i * 2 * Math.PI / numPoints)
      const x = centerX + radius * Math.cos(angle)
      const y = centerY + radius * Math.sin(angle)
      points.push([x, y])
    }
    return points
  }

  // Calculate consistent angle for each skill position
  const getAngleForSkill = (index, numSkills) => {
    const startAngle = -Math.PI / 2 // Start from top
    return startAngle + (index * 2 * Math.PI / numSkills)
  }

  // Generate data polygon points based on skill correctness and levels
  const generateDataPolygon = () => {
    const centerX = 200
    const centerY = 200
    const maxRadius = 160
    const numSkills = Math.min(subskillData?.length || 5, 5)

    const dataPoints = []
    for (let i = 0; i < numSkills; i++) {
      const skillData = getSkillDataForPosition(i)

      let radiusMultiplier
      if (skillData.is_correct) {
        // Strong skills go to outer rings (0.75-1.0 of maxRadius)
        radiusMultiplier = 0.75 + (skillData.skill_level * 0.08) // 0.83-0.99
      } else {
        // Gap skills go to inner rings (0.35-0.65 of maxRadius)
        radiusMultiplier = 0.35 + (skillData.skill_level * 0.1) // 0.45-0.65
      }

      const actualRadius = maxRadius * radiusMultiplier
      const angle = getAngleForSkill(i, numSkills)

      const x = centerX + actualRadius * Math.cos(angle)
      const y = centerY + actualRadius * Math.sin(angle)
      dataPoints.push([x, y])
    }
    return dataPoints
  }

  // Label positioning exactly along pentagon grid lines

  const getBadgeType = (skillData) => {
    if (skillData.is_correct) {
      return { type: 'strong', bgColor: 'rgba(84,178,38,0.1)', textColor: '#54b226', icon: 'https://img.learntube.ai/public-assets/personalise/Vector%20(18).webp' }
    } else {
      return { type: 'poor', bgColor: 'rgba(255,153,0,0.2)', textColor: '#f90', icon: 'https://img.learntube.ai/public-assets/personalise/Vector%20(19).webp' }
    }
  }

  return (
    <div className="bg-[rgba(64,106,255,0.1)] rounded-3xl p-4 sm:p-6 lg:p-8 w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-white to-[#ffe394] bg-clip-text text-transparent capitalize">
            Skill Radar
          </h2>
          <p className="text-xs sm:text-sm lg:text-base text-white/30">
            Visualising your overall Proficiency
          </p>
        </div>
      </div>

      {/* Dynamic Radar Chart Container - Responsive */}
      <div className="relative flex items-center justify-center mb-6 sm:mb-8">
        <div className="relative w-full max-w-[280px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-[450px] aspect-square">
          {/* SVG Chart */}
          <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
            {/* Background grid - concentric polygons */}
            {[50, 90, 130, 170].map((radius, index) => {
              const numSkills = Math.min(subskillData?.length || 5, 5)
              const points = generatePolygonPoints(200, 200, radius, numSkills)
              return (
                <polygon
                  key={`grid-${index}`}
                  points={points.map(p => p.join(',')).join(' ')}
                  fill="none"
                  stroke="rgba(131,190,255,0.2)"
                  strokeWidth="1"
                  strokeDasharray="4,4"
                />
              )
            })}

            {/* Grid lines from center to vertices - aligned with pentagon */}
            {Array.from({ length: Math.min(subskillData?.length || 5, 5) }, (_, i) => {
              const numSkills = Math.min(subskillData?.length || 5, 5)
              const angle = getAngleForSkill(i, numSkills)
              const x2 = 200 + 170 * Math.cos(angle)
              const y2 = 200 + 170 * Math.sin(angle)
              return (
                <g key={`gridline-group-${i}`}>
                  <line
                    x1="200"
                    y1="200"
                    x2={x2}
                    y2={y2}
                    stroke="rgba(131,190,255,0.2)"
                    strokeWidth="1"
                    strokeDasharray="2,2"
                  />
                  {/* Small circle at the end of each grid line for alignment reference */}
                  <circle
                    cx={x2}
                    cy={y2}
                    r="2"
                    fill="rgba(131,190,255,0.3)"
                  />
                </g>
              )
            })}

            {/* Dynamic data polygon */}
            <polygon
              points={generateDataPolygon().map(p => p.join(',')).join(' ')}
              fill="rgba(64,106,255,0.3)"
              stroke="#4169e1"
              strokeWidth="2"
            />
          </svg>

          {/* Data point badges as HTML elements for better cross-browser compatibility */}
          {generateDataPolygon().map((point, index) => {
            const skillData = getSkillDataForPosition(index)
            const badge = getBadgeType(skillData)

            // Convert SVG coordinates to percentage positions
            const x = (point[0] / 400) * 100
            const y = (point[1] / 400) * 100

            return (
              <div
                key={`datapoint-badge-${index}`}
                className="absolute pointer-events-none"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                  WebkitTransform: 'translate(-50%, -50%)',
                  MozTransform: 'translate(-50%, -50%)',
                  msTransform: 'translate(-50%, -50%)',
                  zIndex: 10
                }}
              >
                <div
                  className="backdrop-blur-[3px] flex flex-col gap-1 items-center justify-center px-1 rounded-[60px] w-[34px] h-[34px]"
                  style={{
                    backgroundColor: badge.bgColor
                  }}
                >
                  <div className="w-[7px] h-[5px] flex-shrink-0">
                    <img alt="" className="block max-w-none w-full h-full object-contain" src={badge.icon} />
                  </div>
                  <div
                    className="text-[8px] font-normal text-center leading-none whitespace-nowrap"
                    style={{
                      color: badge.textColor,
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale'
                    }}
                  >
                    {badge.type === 'strong' ? 'Strong' : 'Poor'}
                  </div>
                </div>
              </div>
            )
          })}

          {/* HTML Div Labels - Better positioning control */}
          {Array.from({ length: Math.min(subskillData?.length || 5, 5) }, (_, index) => {
            const numSkills = Math.min(subskillData?.length || 5, 5)
            const angle = getAngleForSkill(index, numSkills)

            // Convert SVG coordinates to percentage positions
            const radius = 46.25 // 185/400 * 100 = 46.25% of container
            const centerX = 50 // 50% (center)
            const centerY = 50 // 50% (center)

            const x = centerX + radius * Math.cos(angle)
            const y = centerY + radius * Math.sin(angle)

            let rotation = 0
            let transformOrigin = 'center center'
            let additionalX = 0
            let additionalY = 0

            if (index === 0) { // Top position - horizontal
              rotation = 0
              additionalY = -3
            } else if (index === 1) { // Top-right position - vertical
              rotation = 90
              additionalX = 4
            } else if (index === 2) { // Bottom-right position - horizontal
              rotation = 0
              additionalX = 3
              additionalY = 8
            } else if (index === 3) { // Bottom-left position - horizontal
              rotation = 0
              additionalX = -3
              additionalY = 8
            } else if (index === 4) { // Top-left position - vertical
              rotation = -90
              additionalX = -4
            }

            return (
              <div
                key={`div-label-${index}`}
                className={`absolute text-[rgba(131,190,255,0.8)]  font-medium ${index === 2 || index === 3 ? 'w-[120px] md:w-[160px]' : 'w-[180px]'} text-center pointer-events-none`}
                style={{
                  left: `${x + additionalX}%`,
                  top: `${y + additionalY}%`,
                  transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                  transformOrigin: transformOrigin,
                  fontSize: 'clamp(11px, 2.5vw, 14px)'
                }}
              >
                {getSkillName(index)}
              </div>
            )
          })}
        </div>
      </div>

      {/* Statistics - More responsive */}
      <div className="flex gap-2 sm:gap-3 lg:gap-4 items-center w-full">
        <div className="bg-[rgba(64,106,255,0.1)] flex-1 flex items-center p-2 sm:p-3 lg:p-4 rounded-xl">
          <div className="flex flex-col gap-1.5 w-full">
            <div className="text-sm sm:text-base lg:text-lg font-bold text-white tracking-[0.32px]">
              {masteredSkills}/5
            </div>
            <div className="text-xs sm:text-sm text-white/50 leading-[1.4]">
              Skills mastered
            </div>
          </div>
        </div>

        <div className="bg-[rgba(64,106,255,0.1)] flex-1 flex items-center p-2 sm:p-3 lg:p-4 rounded-xl">
          <div className="flex flex-col gap-1.5 w-full">
            <div className="text-sm sm:text-base lg:text-lg font-bold text-white tracking-[0.32px]">
              {gapsDetected}
            </div>
            <div className="text-xs sm:text-sm text-white/50 leading-[1.4]">
              Skill gaps detected
            </div>
          </div>
        </div>
      </div>
    </div>

  )
}

export default SkillRadarChart